import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ClearRoundedIcon from '@material-ui/icons/ClearRounded';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import classnames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Button from '../../Button/Button';
import Checkbox from '../../Checkbox/Checkbox';
import Chip from '../../Chip/Chip';
import MobileModal from '../../MobileModal/MobileModal';

import useStyles from '../styles';
import { IMultiSelectProps } from '../types';

const MultiSelect = ({
  value,
  onChange,
  placeholder,
  error,
  helperText,
  options = [],
  label,
  disabled,
  fullWidth,
  width,
  showSelectAll,
  hideDeleteAll,
  headerComponent,
  footerComponent,
  renderOption,
  selectAllContent,
  deselectAllContent,
  mobileModalTitle,
  classes: propClasses = {},
  renderSelectedValue,
  allOptionsSelectedLabel = 'All from the list',
  selectAllState = false,
  onSelectAllChange,
}: IMultiSelectProps) => {
  const isDesktop = useMediaQuery('(min-width: 767px)');
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const offeringWrapRef = useRef<HTMLDivElement>(null);
  const [valueCache, setValueCache] = useState<string[]>(value ?? []);
  const hasInitializedAllRef = useRef(false);

  // Sync value prop
  useEffect(() => {
    setValueCache(value ?? []);
  }, [value]);

  // Auto-select all on mount if selectAllState is true
  useEffect(() => {
    if (!options.length || !selectAllState || hasInitializedAllRef.current)
      return;

    const allValues = options.map((o) => o.value);
    setValueCache(allValues);
    onChange?.(allValues, { isAllSelected: true });

    // Notify parent about select-all
    const totalCount = options.length;
    onSelectAllChange?.({
      checked: totalCount > 0,
      selectedCount: totalCount,
      totalCount,
    });

    hasInitializedAllRef.current = true;
  }, [options, selectAllState, onChange, onSelectAllChange]);

  // Keep select-all true if new options are added
  useEffect(() => {
    if (!selectAllState) return; // only if select-all is true

    const allValues = options.map((o) => o.value);

    // If some new options exist that aren't in valueCache
    const newOptions = allValues.filter((v) => !valueCache.includes(v));
    if (newOptions.length > 0) {
      const updatedValues = [...valueCache, ...newOptions];
      setValueCache(updatedValues);
      onChange?.(updatedValues);
      onSelectAllChange?.({
        checked: updatedValues.length === options.length,
        selectedCount: updatedValues.length,
        totalCount: options.length,
      });
    }
  }, [options, selectAllState, valueCache, onChange, onSelectAllChange]);

  // Handle toggles
  const handleToggle = useCallback(
    (toggleItem: string, manipulateCache = true) =>
      () => {
        if (disabled) return;

        let updatedValues = [...valueCache];

        if (toggleItem === 'ALL') {
          const selectAll = valueCache.length !== options.length;
          updatedValues = selectAll ? options.map((o) => o.value) : [];
        } else {
          const index = updatedValues.indexOf(toggleItem);
          if (index === -1) updatedValues.push(toggleItem);
          else updatedValues.splice(index, 1);
        }

        if (manipulateCache) setValueCache(updatedValues);
        onChange?.(updatedValues);

        // Trigger onSelectAllChange
        const totalCount = options.length;
        const selectedCount = updatedValues.length;
        const checked = totalCount > 0 && selectedCount === totalCount;

        onSelectAllChange?.({
          checked,
          selectedCount,
          totalCount,
        });
      },
    [valueCache, options, disabled, onChange, onSelectAllChange]
  );

  const totalCount = options.length;
  const selectedCount = valueCache.length;
  const isAllSelected = totalCount > 0 && selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;
  const isEmpty = !valueCache.length;

  const classes = useStyles({
    showPlaceholder: isEmpty,
    error,
    fullWidth,
    width: fullWidth ? '100%' : width,
    offeringWrapWidth: offeringWrapRef.current?.offsetWidth,
  });

  const valueMap = useMemo(
    () =>
      options.reduce((acc: Record<string, any>, option) => {
        acc[option.value] = option;
        return acc;
      }, {}),
    [options]
  );

  const valueSelectionMap = useMemo(
    () =>
      valueCache.reduce((acc: Record<string, boolean>, curr) => {
        acc[curr] = true;
        return acc;
      }, {}),
    [valueCache]
  );

  const onOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => setAnchor(event.currentTarget),
    []
  );

  const onClose = useCallback(() => {
    setAnchor(null);
  }, []);

  const handleClearValueCache = useCallback(() => {
    setValueCache(value ?? []);
  }, [value]);

  const handleClose = useCallback(() => {
    handleClearValueCache();
    onClose();
  }, [handleClearValueCache, onClose]);

  const onApply = useCallback(() => {
    onChange(valueCache);
    onClose();
  }, [onChange, onClose, valueCache]);

  const content = (
    <div
      className={classnames(
        classes.contentWrap,
        classes.scroll,
        isDesktop && classes.desktopContent,
        propClasses.contentWrap
      )}
    >
      {headerComponent}

      {showSelectAll && !!options.length && (
        <div
          onClick={handleToggle('ALL')}
          className={classnames(
            classes.selectAll,
            propClasses.selectAllWrapper
          )}
        >
          <Checkbox
            size="large"
            checked={isAllSelected}
            indeterminate={isIndeterminate}
          />
          <span
            className={classnames(classes.font_14, propClasses.selectAllText)}
          >
            {isAllSelected ? deselectAllContent : selectAllContent}
          </span>
        </div>
      )}

      {options.map((option, index, arr) => {
        if (!renderOption)
          return <div className={propClasses.option}>{option.label}</div>;
        return renderOption({
          option,
          value: valueCache,
          onChange: handleToggle(option.value),
          isLast: index === arr.length - 1,
          isSelected: Boolean(valueSelectionMap[option.value]),
          index,
        });
      })}

      {footerComponent}
    </div>
  );

  const _renderSelectedValue = () => {
    if (isEmpty) return placeholder;

    if (renderSelectedValue)
      return renderSelectedValue({
        selectedValue: valueCache,
        selectedOption: valueMap?.[valueCache?.[0]] ?? null,
      });

    if (showSelectAll && isAllSelected) return allOptionsSelectedLabel;

    return (
      <div className={classes.chips}>
        {valueCache.map((item: string) => (
          <Chip
            key={item}
            label={valueMap[item]?.label}
            chipIcon={
              <ClearRoundedIcon onMouseDown={(e) => e.stopPropagation()} />
            }
            chipClassName={classes.filterChip}
            onDelete={handleToggle(item, false)}
          />
        ))}
      </div>
    );
  };

  return (
    <div
      ref={offeringWrapRef}
      className={classnames(classes.outerWrap, propClasses.wrapper)}
    >
      {label && (
        <div
          className={classnames(
            classes.formTitle,
            classes.mb_2,
            propClasses.label
          )}
        >
          {label}
        </div>
      )}

      <div
        className={classnames(classes.offeringDropWrap, propClasses.dropdown)}
        onClick={(e) => (disabled ? null : onOpen(e))}
      >
        <div
          className={classnames(
            classes.selectedTitles,
            classes.multiSelectTitles
          )}
        >
          {_renderSelectedValue()}
        </div>

        {!hideDeleteAll && (
          <IconButton
            component="span"
            className={classes.deleteAllChipsBtn}
            onClick={(event) => {
              event.stopPropagation();
              if (disabled) return;
              if (onChange) onChange([]);
              onSelectAllChange?.({
                checked: false,
                selectedCount: 0,
                totalCount,
              });
            }}
          >
            <CloseRoundedIcon className={classes.crossIcon} />
          </IconButton>
        )}

        <KeyboardArrowDownRoundedIcon className={classes.selectIcon} />
      </div>

      {helperText && (
        <div
          className={classnames(
            classes.helper,
            classes[error ? 'errorWrapper' : 'helperWrapper'],
            propClasses.helperTextWrapper
          )}
        >
          {error && (
            <WarningRoundedIcon
              preserveAspectRatio="none"
              viewBox="3 4 19.06 17.01"
              className={classnames(classes.errorIcon, propClasses.errorIcon)}
            />
          )}
          <span
            className={classnames(
              classes[error ? 'errorText' : 'helperText'],
              propClasses.helperText
            )}
          >
            {helperText}
          </span>
        </div>
      )}

      {isDesktop ? (
        <Popover
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          classes={{
            paper: classnames(
              classes.popover_paper,
              classes.scroll,
              propClasses.popoverPaper
            ),
          }}
        >
          {content}
          <Button
            fullWidth
            size="thin"
            onClick={onApply}
            buttonWrapperClassName={classes.popoverApplyBtn}
          >
            Apply
          </Button>
        </Popover>
      ) : (
        <MobileModal
          open={anchor}
          onClose={handleClose}
          header={mobileModalTitle}
          paperClassName={classnames(
            classes.drawerPaper,
            propClasses.mobileModalPaper
          )}
          primaryAction={{ label: 'Apply', onClick: onApply }}
        >
          {content}
        </MobileModal>
      )}
    </div>
  );
};

export default MultiSelect;
